import { type ReactNode, useState } from 'react'
import { useSnackbar } from 'notistack'

import { List, ListItem, Typography } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'

import type { WithSnackbarMessages } from '../../actions/index.types'
import { CheckboxMenuItem } from '../CheckboxMenuItem/CheckboxMenuItem'
import { useCheckboxMenuContext } from '../context/CheckboxMenuContext'
import type { CheckboxMenuItemLike } from '../index.types'

export type CheckboxMenuSelectedItemProps<Data extends Record<string, unknown>> =
  CheckboxMenuItemLike & {
    onAction: (items: Data[]) => Promise<void> | void
    renderItem: (item: Data) => ReactNode | string
    confirmationTitle?: string
    confirmationMessage?: string
    disabled?: boolean
    actionLabel?: string
    icon?: React.ReactElement
  } & WithSnackbarMessages<Data[]>

/** Component to be used as a base for creating new CheckboxMenuSelectedItems if messages need to be overwritten. NOT for standalone use (eg. delete, suspend, ...) */
export const CheckboxMenuSelectedItem = <Data extends Record<string, unknown>>({
  onAction,
  renderItem,
  successMessage = 'Action completed successfully',
  errorMessage = 'Failed to complete action',
  confirmationTitle = 'Confirm Action',
  confirmationMessage = 'Are you sure you want to perform this action on the following items?',
  disabled = false,
  actionLabel = 'Action',
  icon,
  ...menuProps
}: CheckboxMenuSelectedItemProps<Data>) => {
  const { enqueueSnackbar } = useSnackbar()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { rows, closeMenu, deselectAll } = useCheckboxMenuContext<Data>()

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleAction = async () => {
    try {
      await onAction(rows)
      const message = typeof successMessage === 'function' ? successMessage(rows) : successMessage
      enqueueSnackbar(message, { variant: 'success' })
    } catch (e) {
      const actualErrorMessage = e instanceof Error ? e.message : String(e)
      const fallbackMessage = typeof errorMessage === 'function' ? errorMessage(rows) : errorMessage
      const message = actualErrorMessage || fallbackMessage
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      deselectAll?.()
      closeMenu?.()
      setDialogOpen(false)
    }
  }

  return (
    <>
      <CheckboxMenuItem
        onClick={handleOpenDialog}
        text={actionLabel}
        icon={icon}
        disabled={disabled}
        {...menuProps}
      />
      <ConsoleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={confirmationTitle}
        SaveButtonProps={{
          text: 'Confirm',
          onClick: handleAction
        }}
        CloseButtonProps={{
          text: 'Cancel'
        }}
      >
        <Typography>{confirmationMessage}</Typography>
        {rows.length > 0 && (
          <List
            sx={{
              maxHeight: '300px',
              overflow: 'auto',
              mt: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              '& > li:first-of-type': { pt: 0 },
              '& > li:last-of-type': { pb: 0 }
            }}
          >
            {rows.map((item, index) => (
              <ListItem key={index} divider={index < rows.length - 1}>
                {renderItem(item)}
              </ListItem>
            ))}
          </List>
        )}
      </ConsoleDialog>
    </>
  )
}
